import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Chip,
  Divider,
} from '@mui/material';
import { Upload, CloudUpload, Check, Error as ErrorIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../services/api';
import type { ImportProspectRow, ImportProspectsResult } from '../types';

interface CsvRow {
  [key: string]: string;
}

interface ColumnMapping {
  mondayItemId?: string;
  name?: string;
  prospectNumber?: string;
  legalName?: string;
  buyerName?: string;
  generalStatus?: string;
  followUpStatus?: string;
  nextStep?: string;
  phone?: string;
  email?: string;
  city?: string;
  websiteUrl?: string;
  sector?: string;
  assignedSeller?: string;
  lastUpdatedAt?: string;
  branchCount?: string;
  documentUrl?: string;
}

const fieldLabels: Record<keyof ColumnMapping, string> = {
  mondayItemId: 'ID Monday',
  name: 'Nombre *',
  prospectNumber: 'N°',
  legalName: 'Razón Social',
  buyerName: 'Comprador',
  generalStatus: 'Estado General',
  followUpStatus: 'Seguimiento',
  nextStep: 'Next Step',
  phone: 'Teléfono',
  email: 'Email',
  city: 'Ciudad',
  websiteUrl: 'Web / IG',
  sector: 'Sector',
  assignedSeller: 'Gestionado Por',
  lastUpdatedAt: 'Última actualización',
  branchCount: 'Sucursales',
  documentUrl: 'monday Doc v2',
};

export default function ImportProspectosPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'importing' | 'result'>('upload');
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importResult, setImportResult] = useState<ImportProspectsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  // Check feature flag
  useEffect(() => {
    (async () => {
      try {
        const flags = await api.getFeatureFlags();
        const hasFlag = (flags || []).some(f => f.enabled && f.key.toLowerCase() === 'sales-monday-columns');
        if (!hasFlag) {
          navigate('/customers');
          return;
        }
        setAuthorized(true);
      } catch {
        navigate('/customers');
      }
    })();
  }, [navigate]);

  const processFileData = useCallback((data: any[][], fileName: string) => {
    try {
      if (data.length < 2) {
        setError('El archivo debe tener al menos una fila de encabezados y una de datos.');
        return;
      }

      // Find header row
      let headerRowIndex = 0;
      for (let i = 0; i < Math.min(10, data.length); i++) {
        const row = data[i];
        if (row && row.length > 3) {
          const nonEmptyCount = row.filter((c: any) => c !== undefined && c !== null && c !== '').length;
          if (nonEmptyCount > 3) {
            headerRowIndex = i;
            break;
          }
        }
      }

      const headerLine = data[headerRowIndex].map((h: any) => String(h || '').trim());
      setHeaders(headerLine.filter(h => h !== ''));

      const rows: CsvRow[] = [];
      for (let i = headerRowIndex + 1; i < data.length; i++) {
        const values = data[i];
        if (!values || values.length === 0) continue;

        const row: CsvRow = {};
        let hasData = false;
        headerLine.forEach((header: string, idx: number) => {
          if (header) {
            const val = values[idx];
            row[header] = val !== undefined && val !== null ? String(val) : '';
            if (val !== undefined && val !== null && val !== '') hasData = true;
          }
        });
        if (hasData) rows.push(row);
      }

      setCsvData(rows);

      // Auto-detect mappings based on common column names
      const autoMapping: ColumnMapping = {};
      const lowerHeaders = headerLine.map((h: string) => h.toLowerCase());

      const autoMapPatterns: [keyof ColumnMapping, string[]][] = [
        ['mondayItemId', ['id', 'item id', 'monday id', 'id. de elemento']],
        ['name', ['nombre', 'name', 'prospect', 'prospecto']],
        ['prospectNumber', ['n°', 'n ', 'numero', 'number', '#']],
        ['legalName', ['razon social', 'razón social', 'legal name', 'empresa']],
        ['buyerName', ['comprador', 'buyer', 'contacto', 'contact']],
        ['generalStatus', ['estado general', 'general status', 'estado']],
        ['followUpStatus', ['seguimiento', 'follow up', 'follow-up']],
        ['nextStep', ['next step', 'proximo paso', 'próximo paso']],
        ['phone', ['telefono', 'teléfono', 'tel', 'phone', 'celular']],
        ['email', ['email', 'e-mail', 'correo', 'mail']],
        ['city', ['ciudad', 'city', 'localidad']],
        ['websiteUrl', ['web', 'ig', 'web / ig', 'website', 'instagram']],
        ['sector', ['sector', 'industria', 'rubro']],
        ['assignedSeller', ['gestionado por', 'vendedor', 'asignado', 'seller']],
        ['lastUpdatedAt', ['ultima actualizacion', 'última actualización', 'last updated']],
        ['branchCount', ['sucursales', 'branches', 'locales']],
        ['documentUrl', ['monday doc', 'monday doc v2', 'documento', 'doc url']],
      ];

      for (const [field, patterns] of autoMapPatterns) {
        const matchIdx = lowerHeaders.findIndex((h: string) => patterns.some(p => h.includes(p)));
        if (matchIdx !== -1) {
          autoMapping[field] = headerLine[matchIdx];
        }
      }

      setMapping(autoMapping);
      setStep('mapping');
    } catch (err) {
      setError('Error al procesar el archivo. Verifique el formato.');
    }
  }, []);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];
          processFileData(jsonData, file.name);
        } catch (err) {
          setError('Error al leer el archivo Excel. Verifique que sea un archivo válido.');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);

          const parseCSVLine = (line: string): string[] => {
            const result: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if ((char === ',' || char === ';') && !inQuotes) {
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            result.push(current.trim());
            return result;
          };

          const jsonData = lines.map(line => parseCSVLine(line));
          processFileData(jsonData, file.name);
        } catch (err) {
          setError('Error al procesar el archivo CSV. Verifique el formato.');
        }
      };
      reader.readAsText(file);
    }
  }, [processFileData]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      const input = document.createElement('input');
      input.type = 'file';
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      handleFileUpload({ target: input } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [handleFileUpload]);

  const handleMappingChange = (field: keyof ColumnMapping, value: string) => {
    setMapping(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const getMappedRows = (): ImportProspectRow[] => {
    const parseNumber = (val: string | undefined): number | undefined => {
      if (!val) return undefined;
      const cleaned = val.toString().replace(/[^0-9.,\-]/g, '').replace(',', '.');
      const num = parseFloat(cleaned);
      return isNaN(num) ? undefined : Math.round(num);
    };

    return csvData.map(row => ({
      mondayItemId: mapping.mondayItemId ? row[mapping.mondayItemId]?.trim() : undefined,
      name: mapping.name ? row[mapping.name]?.trim() : undefined,
      prospectNumber: mapping.prospectNumber ? row[mapping.prospectNumber]?.trim() : undefined,
      legalName: mapping.legalName ? row[mapping.legalName]?.trim() : undefined,
      buyerName: mapping.buyerName ? row[mapping.buyerName]?.trim() : undefined,
      generalStatus: mapping.generalStatus ? row[mapping.generalStatus]?.trim() : undefined,
      followUpStatus: mapping.followUpStatus ? row[mapping.followUpStatus]?.trim() : undefined,
      nextStep: mapping.nextStep ? row[mapping.nextStep]?.trim() : undefined,
      phone: mapping.phone ? row[mapping.phone]?.trim() : undefined,
      email: mapping.email ? row[mapping.email]?.trim() : undefined,
      city: mapping.city ? row[mapping.city]?.trim() : undefined,
      websiteUrl: mapping.websiteUrl ? row[mapping.websiteUrl]?.trim() : undefined,
      sector: mapping.sector ? row[mapping.sector]?.trim() : undefined,
      assignedSeller: mapping.assignedSeller ? row[mapping.assignedSeller]?.trim() : undefined,
      lastUpdatedAt: mapping.lastUpdatedAt ? row[mapping.lastUpdatedAt]?.trim() : undefined,
      branchCount: parseNumber(mapping.branchCount ? row[mapping.branchCount] : undefined),
      documentUrl: mapping.documentUrl ? row[mapping.documentUrl]?.trim() : undefined,
    })).filter(row => row.name && row.name.length > 0);
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setStep('importing');

    try {
      const rows = getMappedRows();
      const result = await api.importProspects({
        rows,
        skipDuplicates,
        updateExisting,
      });
      setImportResult(result);
      setStep('result');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al importar prospectos');
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setStep('upload');
    setCsvData([]);
    setHeaders([]);
    setMapping({});
    setImportResult(null);
    setError(null);
  };

  const mappedRows = getMappedRows();

  if (!authorized) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <LinearProgress sx={{ width: 200 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        Importar Prospectos desde Monday.com
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Paso 1: Cargar archivo</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Exporta tus prospectos desde Monday.com (Excel o CSV) y cargalos aquí. El sistema detectará automáticamente las columnas.
            </Typography>

            <Box
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
              component="label"
            >
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={handleFileUpload}
              />
              <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2, display: 'block' }} />
              <Typography variant="h6" color="text.secondary">
                Arrastrá un archivo Excel o CSV aquí
              </Typography>
              <Typography variant="body2" color="text.secondary">
                o hacé clic para seleccionar (.xlsx, .xls, .csv)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'mapping' && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Paso 2: Mapear columnas</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Asociá cada campo con la columna correspondiente del archivo. El campo "Nombre" es obligatorio.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              {(Object.keys(fieldLabels) as (keyof ColumnMapping)[]).map((field) => (
                <TextField
                  key={field}
                  select
                  fullWidth
                  size="small"
                  label={fieldLabels[field]}
                  value={mapping[field] || ''}
                  onChange={(e) => handleMappingChange(field, e.target.value)}
                >
                  <MenuItem value="">
                    <em>No mapear</em>
                  </MenuItem>
                  {headers.map((header) => (
                    <MenuItem key={header} value={header}>{header}</MenuItem>
                  ))}
                </TextField>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ mb: 2 }}>Opciones de importación</Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                  />
                }
                label="Omitir prospectos duplicados (por ID Monday)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    disabled={skipDuplicates}
                  />
                }
                label="Actualizar prospectos existentes"
              />
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetImport}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={() => setStep('preview')}
                disabled={!mapping.name}
              >
                Vista previa
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Paso 3: Vista previa</Typography>
              <Chip label={`${mappedRows.length} prospectos`} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Verificá que los datos se vean correctos antes de importar.
            </Typography>

            <TableContainer sx={{ maxHeight: 400, mb: 3 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Razón Social</TableCell>
                    <TableCell>Comprador</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Ciudad</TableCell>
                    <TableCell>Gestionado Por</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappedRows.slice(0, 50).map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.name || '-'}</TableCell>
                      <TableCell>{row.legalName || '-'}</TableCell>
                      <TableCell>{row.buyerName || '-'}</TableCell>
                      <TableCell>{row.generalStatus || '-'}</TableCell>
                      <TableCell>{row.phone || '-'}</TableCell>
                      <TableCell>{row.city || '-'}</TableCell>
                      <TableCell>{row.assignedSeller || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {mappedRows.length > 50 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Mostrando 50 de {mappedRows.length} prospectos
              </Typography>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setStep('mapping')}>
                Volver
              </Button>
              <Button
                variant="contained"
                startIcon={<Upload />}
                onClick={handleImport}
              >
                Importar {mappedRows.length} prospectos
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Importing */}
      {step === 'importing' && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Importando prospectos...</Typography>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Esto puede tardar unos segundos dependiendo de la cantidad de datos.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Result */}
      {step === 'result' && importResult && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              {importResult.success ? (
                <Check sx={{ fontSize: 48, color: 'success.main' }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
              )}
              <Box>
                <Typography variant="h6">
                  {importResult.success ? 'Importación completada' : 'Importación con errores'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {importResult.created} creados, {importResult.updated} actualizados, {importResult.skipped} omitidos
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="success.main">{importResult.created}</Typography>
                <Typography variant="body2" color="text.secondary">Creados</Typography>
              </Card>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="info.main">{importResult.updated}</Typography>
                <Typography variant="body2" color="text.secondary">Actualizados</Typography>
              </Card>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="warning.main">{importResult.skipped}</Typography>
                <Typography variant="body2" color="text.secondary">Omitidos</Typography>
              </Card>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="error.main">{importResult.errors.length}</Typography>
                <Typography variant="body2" color="text.secondary">Errores</Typography>
              </Card>
            </Stack>

            {importResult.errors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Errores:</Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                  {importResult.errors.map((err, idx) => (
                    <Typography key={idx} variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                      {err}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetImport}>
                Importar otro archivo
              </Button>
              <Button variant="contained" href="/customers">
                Ver clientes
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
